import { type VariableADTvalue, VariableType } from '@lighthouse/core'
import type { FlowNode } from '@lighthouse/shared'
import {
    addresseeFieldType,
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    getUserDatasourceOptions,
    innerTypeToFieldTypeMapInCondition,
    PAGE_LINK_OPTION,
    type StationMessageActionConfig,
    useApplicationContext,
    useAtomData,
    USER_DATASOURCE
} from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import { find } from 'rambda'
import { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type StationMessageCardProps = {
    config: StationMessageActionConfig
}
const StationMessageCard = ({ config }: StationMessageCardProps) => {
    const { title, content, addresseeIds } = config
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const workflowList = useAtomData(workflowListAtom)
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const extractDataTitle = useMemo(() => extractDataFn(title), [title])
    const extractDataContent = useMemo(() => extractDataFn(content), [content])

    const nodesTitle = useNodesData(extractDataTitle.nodeIds) as FlowNode[]
    const nodesContent = useNodesData(extractDataContent.nodeIds) as FlowNode[]

    const nodeIdsAddressee = useMemo(() => {
        const arr: string[] = []
        if (addresseeIds.length) {
            addresseeIds.forEach(item => {
                if (item.type === VariableType.UPSTREAM) {
                    const nodeId = item?.upstreamVariable?.nodeId
                    if (nodeId) {
                        arr.push(nodeId)
                    }
                }
            })
        }
        return arr
    }, [addresseeIds])

    const nodesAddressee = useNodesData(nodeIdsAddressee) as FlowNode[]

    const userOptionAddressee = useMemo(() => {
        const userDataSource = find(item => item.id === USER_DATASOURCE, dataSourceList)
        return getUserDatasourceOptions({
            dataSource: userDataSource
        })
    }, [dataSourceList])

    const upstreamOptionsTitle = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesTitle || []),
                      allUpstreamNodes: nodesTitle
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesTitle]
    )

    const upstreamOptionsContent = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodesContent || []),
                      allUpstreamNodes: nodesContent
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodesContent]
    )

    const upstreamOptionsAddressee = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: nodesAddressee || [],
                allUpstreamNodes: nodesAddressee || [],
                dataSourceList
            }),
        [dataSourceList, nodesAddressee, workflowList]
    )

    const textListTitle = useMemo(
        () =>
            extractDataTitle.objects.map(item => {
                if (item.type === 'text') {
                    return item.value
                }
                return textRichVariableName({
                    userOption,
                    upstreamOptions: upstreamOptionsTitle,
                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                    data: item
                })
            }),
        [extractDataTitle.objects, userOption, upstreamOptionsTitle, pageOptions, isNotAutomation]
    )

    const textListContent = useMemo(
        () =>
            extractDataContent.objects.map(item => {
                if (item.type === 'text') {
                    return item.value
                }
                return textRichVariableName({
                    userOption,
                    upstreamOptions: upstreamOptionsContent,
                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                    data: item
                })
            }),
        [extractDataContent.objects, userOption, upstreamOptionsContent, pageOptions, isNotAutomation]
    )

    const textListAddressee = useMemo(() => {
        const arr: { label: string; value: string }[] = []
        addresseeIds.forEach(item => {
            if (item.type === VariableType.VALUE) {
                const valueUserId =
                    item.valueVariable?.value && Array.isArray(item.valueVariable?.value) ? item.valueVariable?.value?.[0] : ''
                if (item?.valueVariable?.type === 'role') {
                    const role = find(item => item.id === valueUserId, roleOptions || [])
                    arr.push({
                        label: '角色',
                        value: role?.name || ''
                    })
                } else if (item?.valueVariable?.type === 'department') {
                    const department = find(item => item.id === valueUserId, departmentOptions || [])
                    arr.push({
                        label: '部门',
                        value: department?.name || ''
                    })
                } else {
                    const person = find(item => item.id === valueUserId, personOptions || [])
                    if (valueUserId === '{currentUserId}') {
                        arr.push({
                            label: '成员',
                            value: '当前用户'
                        })
                    } else {
                        arr.push({
                            label: '成员',
                            value: person?.name || ''
                        })
                    }
                }
            } else if (item.type === VariableType.USER || item.type === VariableType.UPSTREAM) {
                const val = textRichVariableName({
                    userOption: userOptionAddressee,
                    upstreamOptions: upstreamOptionsAddressee,
                    pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                    data: item as VariableADTvalue
                })
                arr.push({
                    label: '',
                    value: val || ''
                })
            }
        })
        return arr
    }, [
        addresseeIds,
        userOptionAddressee,
        departmentOptions,
        roleOptions,
        personOptions,
        isNotAutomation,
        pageOptions,
        upstreamOptionsAddressee
    ])

    if (!textListTitle.length && !textListContent.length && !textListAddressee.length) {
        return null
    }

    return (
        <CM.CardContainer>
            {textListTitle && textListTitle.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>标题</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {textListTitle.map((text, index) =>
                                text?.startsWith('{') && text?.endsWith('}') ? (
                                    <SC.Text key={index} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                ) : (
                                    <SC.Text key={index}>{text}</SC.Text>
                                )
                            )}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}

            {textListContent && textListContent.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>正文</SC.TypeName>
                    <CM.FieldItemContent>
                        <CM.FieldItemTitle>
                            {textListContent.map((text, index) =>
                                text?.startsWith('{') && text?.endsWith('}') ? (
                                    <SC.Text key={index} color="var(--color-purple-500)">
                                        {text}
                                    </SC.Text>
                                ) : (
                                    <SC.Text key={index}>{text}</SC.Text>
                                )
                            )}
                        </CM.FieldItemTitle>
                    </CM.FieldItemContent>
                </CM.CardItemContainer>
            )}

            {textListAddressee && textListAddressee.length > 0 && (
                <CM.CardItemContainer>
                    <SC.TypeName>收件人</SC.TypeName>
                    <CM.FieldList>
                        {textListAddressee.map((item, index) => (
                            <CM.FieldItem key={index}>
                                {item.label ? (
                                    <>
                                        <CM.FieldItemTitle>{item.value}</CM.FieldItemTitle>
                                        <SC.FieldItemRightTitle>{item.label}</SC.FieldItemRightTitle>
                                    </>
                                ) : (
                                    <CM.FieldItemTitle color="var(--color-purple-500)">{item.value}</CM.FieldItemTitle>
                                )}
                            </CM.FieldItem>
                        ))}
                    </CM.FieldList>
                </CM.CardItemContainer>
            )}
        </CM.CardContainer>
    )
}

export default StationMessageCard
